"use client";

import artworkApi from "@/api/artwork";
import templateApi from "@/api/template";
import ResourceGrantDialog from "@/components/ResourceGrantDialog";
import EnhancedTable from "@/hocs/EnhancedTable";
import { Artwork } from "@/interfaces/artwork.interface";
import { useToastStore } from "@/stores/toast.store";
import { CONSTANTS_KEYS } from "@/constants/store";
import useTimezoneStore from "@/stores/timezone.store";
import { formatDateWithTimezone } from "@/utils/func";
import Cookies from "js-cookie";
import Link from "next/link";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmPopup } from "primereact/confirmpopup";
import { useClickOutside } from "primereact/hooks";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { RefObject, useEffect, useMemo, useRef, useState } from "react";
const ROWS_PER_PAGE = 12;
const ArtworksList = () => {
  const refetchRef = useRef<(() => void) | null>(null);
  const displayTimeZone = useTimezoneStore((s) => s.timezone);
  const currentUser = useMemo(() => {
    try {
      const raw = Cookies.get(CONSTANTS_KEYS.USER);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);
  const hasArtworkAll = currentUser?.isSuperAdmin === true || (
    (currentUser?.permissionCodes?.includes("artwork:read:all") ?? false) &&
    (currentUser?.permissionCodes?.includes("artwork:update:all") ?? false)
  );
  const canShare = (artwork: Artwork) =>
    hasArtworkAll || artwork.userId === currentUser?._id;
  const [selectedArtworkId, setSelectedArtworkId] = useState<string | null>(
    null,
  );
  const [sharingArtwork, setSharingArtwork] = useState<Artwork | null>(null);
  const onCloneArtwork = (id: string) => {
    artworkApi.cloneArtwork(id).then((res) => {
      console.log("Artwork cloned:", res);
      refetchRef.current?.();
      useToastStore
        .getState()
        .showToast("success", "success", "Artwork cloned successfully");
    });
  };
  const artworkModalRef = useRef<HTMLDivElement>(null);
  useClickOutside(artworkModalRef as RefObject<Element>, () => {
    setShowCreateArtwork(false);
  });
  const handleDeleteArtwork = (
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    id: string,
    name: string,
  ) => {
    setSelectedArtworkId(id);
    confirmPopup({
      target: event.currentTarget,
      message: (
        <>
          Are you sure you want to delete the artwork
          <p className="max-w-2xl">
            <strong>&quot;{name}&quot;?</strong>
          </p>
        </>
      ),
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      className: `${id}-confirm-popup`,
      accept: () => {
        artworkApi.deleteOne(id).then(() => {
          useToastStore
            .getState()
            .showToast("success", "success", "Artwork deleted successfully");
          refetchRef.current?.();
        });
      },
      reject: () => {},
      onHide: () => setSelectedArtworkId(null),
      closeOnEscape: true,
    });
  };
  const [showCreateArtwork, setShowCreateArtwork] = useState(false);
  const CreateNewArtWork = () => {
    const [artworkName, setArtworkName] = useState("");
    const [loading, setLoading] = useState(false);

    const handleCreateArtwork = () => {
      if (!artworkName.trim()) {
        useToastStore
          .getState()
          .showToast("error", "Error", "Artwork name cannot be empty");
        return;
      }
      setLoading(true);
      artworkApi
        .createOne({ name: artworkName })
        .then(() => {
          useToastStore
            .getState()
            .showToast("success", "Success", "Artwork created successfully");
          refetchRef.current?.();
          setArtworkName("");
          setShowCreateArtwork(false);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    };

    return (
      <div
        className="absolute top-4 right-0 z-10 flex h-max w-max items-start gap-2 rounded bg-white p-2 shadow"
        ref={artworkModalRef}
      >
        <div>
          <InputText
            value={artworkName}
            onChange={(e) => setArtworkName(e.target.value)}
            placeholder="Enter artwork name"
            className="mb-2 w-full"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleCreateArtwork();
              }
            }}
          />
          <p className="mt-2 text-sm text-gray-500">
            Note: The artwork name is required to create a new artwork.
          </p>
        </div>

        <Button
          loading={loading}
          label="Create"
          onClick={handleCreateArtwork}
          className="h-max w-max"
          disabled={!artworkName.trim()}
        />
      </div>
    );
  };
  return (
    <div className="flex h-full flex-col rounded-lg bg-white p-6 shadow-lg">
      <div className="relative h-0 w-full"></div>
      <EnhancedTable
        cachedKey="artworks"
        fetchData={artworkApi.getMany}
        itemsPerPage={ROWS_PER_PAGE}
        searchPlaceholder="Search artworks"
        title="Artworks"
        tableStyle={{
          filter: showCreateArtwork ? "blur(4px)" : "none",
        }}
        rowClassName={(rowData) => {
          return rowData._id === selectedArtworkId ? "highlight-table-row" : "";
        }}
        refetchRef={refetchRef}
        suffix={
          <div className="flex flex-col items-end">
            <Button
              onClick={() => setShowCreateArtwork(!showCreateArtwork)}
              className="w-max"
              style={{
                fontWeight: "bold",
              }}
            >
              Create new
            </Button>
            <div
              className="relative h-max w-max"
              style={{ display: showCreateArtwork ? "flex" : "none" }}
            >
              <CreateNewArtWork />
            </div>
          </div>
        }
      >
        <Column
          field="name"
          header="Artwork Name"
          body={(rowData) => RenderArtworkName({ artwork: rowData })}
        />
        {/* <Column
          field="totalTemplate"
          header="Total Templates"
          body={() => {
            return (
              <span className="block w-full text-sm font-bold text-center text-gray-500">
                {0}
              </span>
            );
          }}
          style={{ width: "10%" }}
        /> */}
        <Column
          header="Timestamp"
          field="createdAt"
          body={(rowData: Artwork) => {
            return (
              <p>
                <span
                  className="text-gray-500"
                  style={{
                    fontSize: "0.8rem",
                  }}
                >
                  {formatDateWithTimezone(rowData.createdAt, displayTimeZone)}
                </span>
                <span className="flex h-max w-full items-center justify-center">
                  <i
                    className="pi pi-arrow-down text-sm text-gray-500"
                    style={{
                      fontSize: "0.5rem",
                    }}
                  ></i>
                </span>
                <span
                  className="text-gray-500"
                  style={{
                    fontSize: "0.8rem",
                  }}
                >
                  {formatDateWithTimezone(rowData.updatedAt, displayTimeZone)}
                </span>
              </p>
            );
          }}
          style={{ width: "70px", whiteSpace: "nowrap", textAlign: "start" }}
        />
        <Column
          header="Actions"
          body={(artwork: Artwork) => {
            return (
              <div className="flex items-center justify-end gap-1">
                {canShare(artwork) && (
                  <span
                    className="flex h-10 w-10 cursor-pointer items-center justify-center rounded p-1 transition-all hover:bg-gray-300"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSharingArtwork(artwork);
                    }}
                  >
                    <i
                      className="pi pi-share-alt text-purple-600"
                      style={{
                        fontSize: "1.2rem",
                      }}
                    ></i>
                  </span>
                )}
                <span
                  className="flex h-10 w-10 cursor-pointer items-center justify-center rounded p-1 transition-all hover:bg-gray-300"
                  onClick={() => {
                    onCloneArtwork(artwork._id);
                  }}
                >
                  <i
                    className="pi pi-copy text-blue-600"
                    style={{
                      fontSize: "1.2rem",
                    }}
                  ></i>
                </span>
                <Link
                  href={`/artworks/${artwork._id}`}
                  className="flex h-10 w-10 cursor-pointer items-center justify-center rounded p-1 transition-all hover:bg-gray-300"
                >
                  <i
                    className="pi pi-pen-to-square text-green-600 disabled:text-gray-500"
                    style={{
                      fontSize: "1.2rem",
                    }}
                  ></i>
                </Link>
                <span
                  className="flex h-10 w-10 cursor-pointer items-center justify-center rounded p-1 transition-all hover:bg-gray-300"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteArtwork(e, artwork._id, artwork.name);
                  }}
                >
                  <i
                    className="pi pi-trash text-red-600"
                    style={{
                      fontSize: "1.2rem",
                    }}
                  ></i>
                </span>
              </div>
            );
          }}
          style={{ width: "160px" }}
        />
      </EnhancedTable>
      <ResourceGrantDialog
        visible={Boolean(sharingArtwork)}
        resourceEndpoint="artworks"
        resourceType="artwork"
        resourceId={sharingArtwork?._id}
        resourceName={sharingArtwork?.name}
        ownerId={sharingArtwork?.userId}
        relatedResourceLabel="templates"
        loadRelatedResources={async (artworkId) => {
          const response = await templateApi.getMany({
            page: 1,
            size: 1000,
            artworkId,
            view: "list",
          });
          return (response.data ?? []).map((template) => ({
            resourceEndpoint: "templates",
            resourceType: "template",
            resourceId: template._id,
            name: template.name,
          }));
        }}
        onHide={() => setSharingArtwork(null)}
      />
    </div>
  );
};

export default ArtworksList;

const RenderArtworkName = ({ artwork }: { artwork: Artwork }) => {
  const [isEditing, setIsEditing] = useState(false);
  const artworkRef = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [artworkName, setartworkName] = useState("");
  useEffect(() => {
    setartworkName(artwork.name);
  }, [artwork]);
  const handleClose = () => {
    setIsEditing(false);
  };

  const handleSave = () => {
    artworkApi.updateOne(artwork._id, { name: artworkName }).then(() => {
      setIsEditing(false);
      setartworkName(artworkName);
      useToastStore
        .getState()
        .showToast("success", "success", "Artwork name updated successfully");
    });
  };

  useClickOutside(artworkRef as RefObject<Element>, handleClose);

  useEffect(() => {
    if (isEditing && textAreaRef.current) {
      textAreaRef.current.focus();
      const length = textAreaRef.current.value.length;
      textAreaRef.current.setSelectionRange(length, length);
    }
  }, [isEditing]);

  return (
    <div
      className="relative w-full rounded p-2 transition-all hover:bg-gray-100"
      ref={artworkRef}
    >
      {isEditing ? (
        <InputTextarea
          rows={3}
          ref={textAreaRef}
          cols={30}
          value={artworkName}
          onChange={(e) => setartworkName(e.target.value)}
          className="w-full rounded border p-1"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSave();
            }
          }}
        />
      ) : (
        <span
          className="line-clamp-2 w-full cursor-pointer"
          onClick={() => setIsEditing(true)}
        >
          {artworkName}
        </span>
      )}
      <div
        className={`absolute right-1 bottom-1 gap-2 ${
          isEditing ? "flex" : "hidden"
        }`}
      >
        <i
          className="pi pi-undo cursor-pointer rounded-md bg-white p-2 text-black shadow hover:bg-gray-600 hover:text-white"
          onClick={handleClose}
        ></i>
        <i
          className="pi pi-save cursor-pointer rounded-md bg-white p-2 text-blue-600 shadow hover:bg-blue-600 hover:text-white"
          onClick={handleSave}
        ></i>
      </div>
    </div>
  );
};
